import React, { useEffect, useState } from 'react';
import { useParams }   from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  RiUserLine,
  RiLockLine,
  RiMapPinLine,
  RiShoppingBagLine,
  RiImageEditLine,
  RiMoneyDollarCircleLine,
} from 'react-icons/ri';

import Breadcrumb from '@/components/common/Breadcrumb';
import Card       from '@/components/ui/cards/Card';
import ToastMe    from '@/components/ui/ToastMe';
import { getByIdAdminService } from '@/services/AdminServices';

import ProfileTab  from './tabs/ProfileTab';
import SecurityTab from './tabs/SecurityTab';
import AddressTab  from './tabs/AddressTab';
import OrdersTab   from './tabs/OrdersTab';
import PayrollTab  from './tabs/PayrollTab';

const TAB_ITEMS = [
  { label: 'Profile',   key: 'profile',  Icon: RiUserLine        },
  { label: 'Security',  key: 'security', Icon: RiLockLine        },
  { label: 'Address',   key: 'address',  Icon: RiMapPinLine      },
  { label: 'My Orders', key: 'orders',   Icon: RiShoppingBagLine },
  { label: 'Payroll',   key: 'payroll',  Icon: RiMoneyDollarCircleLine },
];

export default function AdminViewPage({ pageTitle }) {
  const { id: adminId } = useParams();
  const dispatch        = useDispatch();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]    = useState('profile');

  useEffect(() => {
    dispatch(getByIdAdminService(adminId))
      .then(r => setAdmin(r.data?.data))
      .catch(e => ToastMe(e.message || 'Failed to load administrator'))
      .finally(() => setLoading(false));
  }, [adminId, dispatch]);

  if (loading) return <Card className="p-6 text-center">Loading…</Card>;
  if (!admin) return <Card className="p-6 text-center">No administrator found.</Card>;

  return (
    <>
      {/* Breadcrumb */}
      <div className="pt-[18px]">
        <Breadcrumb pageTitle={pageTitle} />
      </div>

      {/* Header */}
      <Card className="rounded-xl border border-[#f0f2f8] bg-white p-6">
        <div className="flex gap-6 items-start">
          {/* Avatar */}
          <div className="shrink-0">
            <div className="w-[120px] h-[120px] rounded-[6px] overflow-hidden border border-slate-200">
              <img
                src={admin.file_name || '/images/default-avatar.png'}
                alt={admin.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Details & Photo Upload */}
          <div className="flex flex-col justify-start">
            <h2 className="text-[28px] leading-8 font-bold tracking-tight text-slate-900">
              {admin.name}
            </h2>
            <span
              className="mt-2 inline-block w-max rounded-[4px]
                         bg-[#f8e8b7] px-[10px] py-[3px]
                         text-[11px] font-semibold uppercase text-[#b38a00] tracking-wide"
            >
              {admin.role_id?.name || 'Administrator'}
            </span>

            <button
              onClick={() => document.getElementById('photoUpload').click()}
              className="mt-6 inline-flex items-center justify-center gap-2
                         h-[44px] w-[220px] rounded-[6px]
                         text-[15px] font-semibold text-white
                         bg-[#0065ff] hover:bg-[#0056e0] transition"
            >
              <RiImageEditLine className="text-[20px]" />
              Upload New Photo
            </button>
            <input id="photoUpload" type="file" className="hidden" />
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="mt-6 px-6">
        <ul className="flex items-center space-x-6">
          {TAB_ITEMS.map(({ key, label, Icon }) => (
            <li key={key}>
              <button
                onClick={() => setTab(key)}
                className={
                  `inline-flex items-center gap-2 px-5 py-2 text-sm font-medium
                   rounded-lg transition ` +
                  (tab === key
                    ? 'bg-[#0f62fe] text-white'
                    : 'text-slate-600 hover:text-slate-900')
                }
              >
                <Icon className="text-base" />
                {label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {tab === 'profile'  && <ProfileTab  admin={admin} />}
        {tab === 'security' && <SecurityTab adminId={adminId} />}
        {tab === 'address'  && <AddressTab  />}
        {tab === 'orders'   && <OrdersTab   adminId={adminId} />}
        {tab === 'payroll'  && <PayrollTab  employeeId={adminId} />}
      </div>
    </>
  );
}
